/*
 * TelemetrySetEditorModal — pflegt ein Telemetrieset: Name, Beschreibung und
 * die Punktliste mit ihren Aufzeichnungsparametern (Konzept §4.2).
 *
 * Ein Set ist eine WIEDERVERWENDBARE Vorlage. Seine Punkte zeigen deshalb
 * nicht auf Datenpunkt-Ids, sondern auf Symbolpfade — dieselbe Kennung, die
 * auf jedem Regler mit demselben Schema gilt. Der Editor macht das sichtbar:
 * die Refs sind Textfelder, nicht ein Baum-Picker eines konkreten Reglers.
 * Vorbefuellen aus einem Regler geht ueber „Aus Regler übernehmen"; was dabei
 * eingesetzt wird, ist der Symbolpfad, nicht die Id.
 *
 * Speichern ersetzt die Punktliste vollstaendig (PUT) und erhoeht die Version
 * des Sets. Gebundene Regler zeigen danach „veraltet", bis das Set erneut
 * angewandt wird — genau dafuer ist die Version da.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import type { LeafDto, NodeDto } from '../api/types';
import { IconCancel, IconSave } from '../icons/HvacIcons';
import './hv-modal.css';

/** Ein Punkt auf dem Draht — Feldnamen wie im Server-DTO. */
export interface TelemetryPointDto {
  datapointRef: string;
  covAbsolute: number | null;
  covPercent: number | null;
  minIntervalSec: number | null;
  heartbeatSec: number | null;
  sortOrder: number;
}

/** Ein Set mit Punkten, wie GET /api/telemetry-sets/{id} es liefert. */
export interface TelemetrySetDetail {
  id: string;
  name: string;
  description: string | null;
  version: number;
  points: TelemetryPointDto[];
  boundControllerCount: number;
  createdAt: string;
  updatedAt: string;
}

interface Props {
  /** Vorhandenes Set zum Bearbeiten, oder null fuer ein neues. */
  set: TelemetrySetDetail | null;
  /** Optional: der Baum eines Reglers, aus dem sich Symbolpfade übernehmen
   *  lassen. Ohne ihn fehlt nur der Komfort — Refs bleiben von Hand
   *  eingebbar, weil ein Set ausdrücklich nicht an einen Regler gebunden ist. */
  suggestFromNodes?: NodeDto[];
  onSaved(set: TelemetrySetDetail): void;
  onClose(): void;
}

/** Zeile im Editor. Zahlen als String, damit ein halb getippter Wert nicht
 *  vorzeitig geparst wird — dasselbe Muster wie im LeafConfigModal. */
interface PointDraft {
  key: string;
  datapointRef: string;
  covAbsolute: string;
  covPercent: string;
  minIntervalSec: string;
  heartbeatSec: string;
}

export function TelemetrySetEditorModal({ set, suggestFromNodes, onSaved, onClose }: Props) {
  const [name, setName] = useState(set?.name ?? '');
  const [description, setDescription] = useState(set?.description ?? '');
  const [points, setPoints] = useState<PointDraft[]>(() => initialPoints(set));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, saving]);

  // Symbolpfade des mitgegebenen Reglers, für die Vorschlagsliste. Genau die
  // Kennung, die der Server beim Anwenden auflöst.
  const suggestions = useMemo(
    () => (suggestFromNodes ? collectSymbolPaths(suggestFromNodes, '') : []),
    [suggestFromNodes],
  );

  const usedRefs = useMemo(
    () => new Set(points.map((p) => normaliseRef(p.datapointRef)).filter((r) => r.length > 0)),
    [points],
  );

  // Doppelte Refs blockieren das Speichern, statt den Server-409 abzuwarten:
  // zwei Zeilen für denselben Datenpunkt wären zwei widersprechende
  // Konfigurationen ohne Schiedsrichter.
  const duplicateRefs = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const p of points) {
      const r = normaliseRef(p.datapointRef);
      if (r.length === 0) continue;
      if (seen.has(r)) dupes.add(r);
      seen.add(r);
    }
    return dupes;
  }, [points]);

  const canSave = name.trim().length > 0
    && duplicateRefs.size === 0
    && points.every((p) => p.datapointRef.trim().length > 0)
    && !saving;

  const addPoint = (ref = '') => setPoints((prev) => [...prev, freshPoint(ref)]);
  const removePoint = (key: string) => setPoints((prev) => prev.filter((p) => p.key !== key));
  const patchPoint = (key: string, field: keyof PointDraft, value: string) =>
    setPoints((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim().length > 0 ? description.trim() : null,
        points: points.map((p, i) => ({
          datapointRef: p.datapointRef.trim(),
          covAbsolute: parseNullableFloat(p.covAbsolute),
          covPercent: parseNullableFloat(p.covPercent),
          minIntervalSec: parseNullableInt(p.minIntervalSec),
          heartbeatSec: parseNullableInt(p.heartbeatSec),
          sortOrder: i,
        })),
      };

      const saved = set
        ? await api.put<TelemetrySetDetail>(`/api/telemetry-sets/${set.id}`, body)
        : await api.post<TelemetrySetDetail>('/api/telemetry-sets', body);

      onSaved(saved);
      onClose();
    } catch (e) {
      setError(describeError(e));
      setSaving(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !saving && onClose()}>
      <div
        class="hv-modal hv-modal--telemetry-set"
        style={{ maxWidth: 900 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">
            {set ? `Telemetrieset bearbeiten (v${set.version})` : 'Telemetrieset anlegen'}
          </h2>
          <button
            type="button"
            class="hv-modal__close"
            onClick={onClose}
            aria-label="Schließen"
            disabled={saving}
          >×</button>
        </div>

        <div class="hv-modal__body">
          <p class="hv-modal__hint">
            Ein Telemetrieset legt fest, <strong>welche</strong> Datenpunkte aufgezeichnet werden
            und mit welchen Schwellen. Es ist auf beliebig viele Regler anwendbar, weil seine
            Punkte über den <strong>Symbolpfad</strong> zeigen und nicht über die Datenpunkt-Id
            eines einzelnen Reglers.
          </p>

          {set && set.boundControllerCount > 0 && (
            <p class="hv-modal__hint">
              Dieses Set läuft auf <strong>{set.boundControllerCount} Regler(n)</strong>. Speichern
              erhöht die Version auf <strong>v{set.version + 1}</strong>; diese Regler gelten
              danach als veraltet, bis das Set dort erneut angewandt wird. Es ändert sich nichts
              am Feld, solange das nicht passiert.
            </p>
          )}

          <div class="hv-modal__field">
            <label for="hv-tset-name">Name</label>
            <input
              id="hv-tset-name"
              type="text"
              value={name}
              placeholder="z. B. Wärmepumpe Standard"
              disabled={saving}
              onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
            />
          </div>

          <div class="hv-modal__field">
            <label for="hv-tset-desc">Beschreibung</label>
            <input
              id="hv-tset-desc"
              type="text"
              value={description}
              placeholder="Wofür ist dieses Set gedacht?"
              disabled={saving}
              onInput={(e) => setDescription((e.currentTarget as HTMLInputElement).value)}
            />
          </div>

          <div class="hv-modal__field">
            <label>Datenpunkte ({points.length})</label>
            {points.length === 0 && (
              <p class="hv-modal__hint">
                Noch keine Punkte. Ein Set ohne Punkte lässt sich speichern, bindet aber nichts.
              </p>
            )}

            {points.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table class="hv-tset-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={THL}>Symbolpfad</th>
                      <th style={THN} title="Absolutes Deadband in kanonischen Einheiten">Deadband</th>
                      <th style={THN} title="Relatives Deadband in Prozent vom letzten gesendeten Wert">%</th>
                      <th style={THN} title="Frühestens alle N Sekunden senden">Min. (s)</th>
                      <th style={THN} title="Senden auch ohne Änderung mind. alle N Sekunden; leer = kein Heartbeat">Heartbeat (s)</th>
                      <th style={{ ...THN, width: 36 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((p) => {
                      const isDupe = duplicateRefs.has(normaliseRef(p.datapointRef));
                      const isEmpty = p.datapointRef.trim().length === 0;
                      return (
                        <tr key={p.key}>
                          <td style={TD}>
                            <input
                              type="text"
                              list="hv-tset-ref-suggestions"
                              value={p.datapointRef}
                              placeholder="heizkreis.1/vorlauf/temperatur"
                              disabled={saving}
                              style={{
                                width: '100%',
                                fontFamily: 'var(--font-mono, monospace)',
                                borderColor: (isDupe || isEmpty) ? 'rgba(220,60,60,0.6)' : undefined,
                              }}
                              onInput={(e) =>
                                patchPoint(p.key, 'datapointRef', (e.currentTarget as HTMLInputElement).value)}
                            />
                            {isDupe && (
                              <div class="hv-modal__error" style={{ marginTop: 2 }}>
                                Dieser Datenpunkt steht zweimal im Set.
                              </div>
                            )}
                          </td>
                          <NumCell draft={p} field="covAbsolute" step="any" min={0}
                                   disabled={saving} onChange={patchPoint} />
                          <NumCell draft={p} field="covPercent" step="any" min={0} max={100}
                                   disabled={saving} onChange={patchPoint} />
                          <NumCell draft={p} field="minIntervalSec" step={1} min={0}
                                   disabled={saving} onChange={patchPoint} />
                          <NumCell draft={p} field="heartbeatSec" step={1} min={0}
                                   disabled={saving} onChange={patchPoint} />
                          <td style={TD}>
                            <button
                              type="button"
                              class="hv-btn hv-btn--sm hv-btn--secondary"
                              title="Punkt entfernen"
                              aria-label={`Punkt ${p.datapointRef || '(leer)'} entfernen`}
                              disabled={saving}
                              onClick={() => removePoint(p.key)}
                            >×</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vorschlagsliste für die Ref-Eingabe: die Symbolpfade des
                Reglers, von dem aus der Dialog geöffnet wurde. Ein Datalist
                und kein Picker, weil ein Set ausdrücklich auch Refs tragen
                darf, die auf DIESEM Regler nicht existieren — es ist eine
                Vorlage für eine Flotte, nicht die Konfiguration eines
                Geräts. */}
            <datalist id="hv-tset-ref-suggestions">
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>

            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button type="button" class="hv-btn hv-btn--sm" disabled={saving}
                      onClick={() => addPoint()}>
                Punkt hinzufügen
              </button>
              {suggestions.length > 0 && (
                <button
                  type="button"
                  class="hv-btn hv-btn--sm"
                  disabled={saving}
                  title="Alle Symbolpfade dieses Reglers als Punkte übernehmen, die noch nicht im Set stehen"
                  onClick={() => setPoints((prev) => [
                    ...prev,
                    ...suggestions.filter((s) => !usedRefs.has(s)).map((s) => freshPoint(s)),
                  ])}
                >
                  Aus Regler übernehmen ({suggestions.filter((s) => !usedRefs.has(s)).length})
                </button>
              )}
            </div>
          </div>

          {error && <p class="hv-modal__error">{error}</p>}
        </div>

        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" onClick={onClose} disabled={saving}>
            <span style={INLINE}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button
            type="button"
            class="hv-btn hv-btn--primary"
            onClick={save}
            disabled={!canSave}
            title={
              name.trim().length === 0 ? 'Name ist Pflicht'
                : duplicateRefs.size > 0 ? 'Doppelte Datenpunkte auflösen'
                : points.some((p) => p.datapointRef.trim().length === 0) ? 'Leere Symbolpfade auffüllen oder entfernen'
                : undefined
            }
          >
            {saving ? 'Speichern…' : <span style={INLINE}><IconSave size={14} />Speichern</span>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// -- Teilstücke -------------------------------------------------------------

const INLINE = { display: 'inline-flex', alignItems: 'center', gap: 6 } as const;
const THL = { textAlign: 'left', fontSize: 11, padding: '4px 6px', opacity: 0.7 } as const;
const THN = { textAlign: 'right', fontSize: 11, padding: '4px 6px', opacity: 0.7, width: 90 } as const;
const TD = { padding: '2px 6px', verticalAlign: 'top' } as const;

/** Eine Zahlenzelle der Punkttabelle. Leer heißt „keine Meinung" und wird
 *  als null gesendet — nicht als 0: ein Heartbeat von 0 wäre ein aktivierter
 *  Kanal, ein Deadband von 0 hieße „jede Änderung senden". */
function NumCell({
  draft, field, step, min, max, disabled, onChange,
}: {
  draft: PointDraft;
  field: 'covAbsolute' | 'covPercent' | 'minIntervalSec' | 'heartbeatSec';
  step: number | 'any';
  min?: number;
  max?: number;
  disabled: boolean;
  onChange(key: string, field: keyof PointDraft, value: string): void;
}) {
  return (
    <td style={TD}>
      <input
        type="number"
        step={step}
        min={min}
        max={max}
        value={draft[field]}
        disabled={disabled}
        placeholder="—"
        style={{ width: '100%', textAlign: 'right' }}
        onInput={(e) => onChange(draft.key, field, (e.currentTarget as HTMLInputElement).value)}
      />
    </td>
  );
}

// -- Helfer -----------------------------------------------------------------

let pointKeySeq = 0;

/**
 * Heartbeat-Vorbelegung fuer einen neu hinzugefuegten Punkt: 15 Minuten.
 *
 * Der Grund ist die Kehrseite des CoV-Gates: ein Punkt ohne Heartbeat, der
 * stillsteht, publiziert einmal und danach nie wieder — am 09.09. auf dem
 * RevPi gemessen als ein ganzes Minutenfenster mit 0 von 1198 publizierten
 * Werten. Fuer die Zeitreihe ist das richtig, fuer jede Alterspruefung nicht.
 *
 * Bewusst hier und nicht als Fallback im Client: `null` heisst weiterhin
 * "Kanal aus", und wer den Punkt wirklich stumm haben will, leert das Feld.
 * Eine Vorbelegung im Formular sieht man und kann sie aendern; ein Default
 * im Client waere eine Zahl, die nirgends steht.
 */
const DefaultHeartbeatSec = '900';

function freshPoint(datapointRef: string): PointDraft {
  pointKeySeq += 1;
  return {
    key: `p${pointKeySeq}`,
    datapointRef,
    covAbsolute: '',
    covPercent: '',
    minIntervalSec: '',
    heartbeatSec: DefaultHeartbeatSec,
  };
}

function initialPoints(set: TelemetrySetDetail | null): PointDraft[] {
  if (!set) return [];
  return set.points.map((p) => {
    pointKeySeq += 1;
    return {
      key: `p${pointKeySeq}`,
      datapointRef: p.datapointRef,
      covAbsolute: p.covAbsolute == null ? '' : String(p.covAbsolute),
      covPercent: p.covPercent == null ? '' : String(p.covPercent),
      minIntervalSec: p.minIntervalSec == null ? '' : String(p.minIntervalSec),
      heartbeatSec: p.heartbeatSec == null ? '' : String(p.heartbeatSec),
    };
  });
}

/**
 * Muss zeichengleich zu TelemetryPoint.NormaliseRef auf dem Server sein:
 * getrimmt, kleingeschrieben, keine leeren Segmente, keine führenden oder
 * abschließenden Trenner. Wird hier nur für die Dublettenwarnung gebraucht —
 * gespeichert wird der Rohwert, und der Server normalisiert autoritativ.
 * Zwei Normalisierungen, die auseinanderlaufen, würden hier eine Dublette
 * durchlassen, die der Server dann mit 400 ablehnt.
 */
export function normaliseRef(datapointRef: string): string {
  return datapointRef
    .split('/')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join('/')
    .toLowerCase();
}

/** Alle Symbolpfade unter einer Node-Liste, in Baumreihenfolge. Genau die
 *  Kennung, die der Server beim Anwenden auflöst. */
export function collectSymbolPaths(nodes: NodeDto[], prefix: string): string[] {
  const out: string[] = [];
  for (const node of nodes) {
    const path = prefix.length === 0 ? node.symbol : `${prefix}/${node.symbol}`;
    for (const leaf of node.leaves) {
      out.push(normaliseRef(`${path}/${leaf.symbol}`));
    }
    out.push(...collectSymbolPaths(node.children, path));
  }
  return out;
}

/** Symbolpfad eines einzelnen Blattes, oder null wenn es im Baum nicht
 *  gefunden wird. */
export function findSymbolPath(nodes: NodeDto[], leafId: string, prefix = ''): string | null {
  for (const node of nodes) {
    const path = prefix.length === 0 ? node.symbol : `${prefix}/${node.symbol}`;
    const hit = node.leaves.find((l: LeafDto) => l.id === leafId);
    if (hit) return normaliseRef(`${path}/${hit.symbol}`);
    const deeper = findSymbolPath(node.children, leafId, path);
    if (deeper) return deeper;
  }
  return null;
}

function parseNullableFloat(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const v = parseFloat(t);
  return Number.isFinite(v) ? v : null;
}

function parseNullableInt(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const v = parseInt(t, 10);
  return Number.isFinite(v) ? v : null;
}

/** Server-Fehler in einen Satz, den der Operator lesen kann. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: string; detail?: string } | null;
    const msg = body?.error ?? body?.detail;
    if (msg) return msg;
    if (e.status === 403) return 'Keine Berechtigung. `plants.schema.edit` fehlt.';
    if (e.status === 404) return 'Nicht gefunden.';
    return `Fehlgeschlagen (${e.status}).`;
  }
  if (e instanceof Error) return e.message;
  return 'Unbekannter Fehler.';
}
